import moment from 'moment';
import { keys } from '../../../../utilities/translator/translation_keys';
import { incidentStatusTypes as incidentStatus } from '../../../../utilities/incidents';
import { statusStringLookup as actionItemStatus } from '../../../../components/action_items/action_item_utilities';
import { observationStatus } from '../../../../utilities/observations';
import { showToast } from '../../../../graphql/cache/modal';
import { toastVariant } from '../../../../constants/misc';
import { vehicleString } from '../../../../utilities';
import i18n from '../../../../i18n';

export const groupLookUp = (group) => {
  const { t } = i18n;
  switch (group) {
    case 'workspaceId':
      return t(keys.report.BY, { variable: t(keys.common.WORKSPACE) });
    case 'creatorId':
      return t(keys.report.BY, { variable: t(keys.common.CREATOR) });
    case 'assigneeId':
      return t(keys.report.BY, { variable: t(keys.common.ASSIGNEE) });
    case 'participants':
      return t(keys.report.BY, { variable: t(keys.common.PARTICIPANTS) });
    case 'inspectionType':
    case 'auditType':
    case 'type':
      return t(keys.report.BY, { variable: t(keys.common.TYPE) });
    case 'subtype':
      return t(keys.report.BY, { variable: t(keys.common.SUBTYPE) });
    case 'status':
    case 'isDraft':
      return t(keys.report.BY, { variable: t(keys.common.STATUS) });
    case 'dateOfIncident':
      return t(keys.report.BY, {
        variable: t(keys.incidents.DATE_OF_INCIDENT),
      });
    case 'lostTime':
      return t(keys.report.BY, { variable: t(keys.incidents.LOST_TIME) });
    case 'nearMiss':
      return t(keys.report.BY, { variable: t(keys.incidents.NEAR_MISS) });
    case 'location':
      return t(keys.report.BY, { variable: t(keys.common.LOCATION) });
    case 'vehicleId':
      return t(keys.report.BY, { variable: t(keys.incidents.ASSET) });
    case 'dateTime':
      return t(keys.report.BY, {
        variable: t(keys.observations.DATE_AND_TIME),
      });
    case 'passed':
      return t(keys.report.BY, { variable: t(keys.report.OUTCOME) });
    default:
      return null;
  }
};

export const modelLookUp = ({
  model,
  t,
  companyNames: { observationName, incidentName, assessmentName },
}) => {
  switch (model) {
    case 'correctiveAction':
      return t(keys.common.CORRECTIVE_ACTION);
    case 'audit':
      return 'Audit';
    case 'actionItem':
      return t(keys.common.ACTION_ITEM);
    case 'inspection':
      return `${assessmentName ?? t(keys.common.ASSESSMENTS)}`;
    case 'incident':
      return `${incidentName ?? t(keys.common.INCIDENT)}`;
    case 'observation':
      return `${observationName ?? t(keys.common.OBSERVATIONS)}`;
    case 'timecard':
      return t(keys.common.TIMECARDS);
    case 'expense':
      return t(keys.common.EXPENSES);
    default:
      return null;
  }
};

export const participantModelLookup = (model) => {
  switch (model) {
    case 'inspection':
      return 'inspectionParticipant';
    case 'actionItem':
      return 'actionItemParticipant';
    case 'observation':
      return 'observationParticipant';
    case 'incident':
      return 'incidentParticipant';
    default:
      return '';
  }
};

export const timeRangeOptions = [
  { label: 'Last 30 Days', value: 'last30', reoccur: '30' },
  { label: 'Last 90 Days', value: 'last90', reoccur: '90' },
  { label: 'Last 365 Days', value: 'last365', reoccur: '365' },
  { label: 'Monthly', value: 'monthly', reoccur: 'MONTHLY' },
  { label: 'Annual', value: 'annual', reoccur: 'ANNUAL' },
  { label: 'Custom', value: 'custom', reoccur: null },
];

export const getTimeRange = (value) => {
  const to = moment();
  const from = moment();
  switch (value) {
    case 'last30':
      return {
        to,
        from: from.subtract(30, 'days'),
        reoccur: '30',
      };
    case 'last90':
      return {
        to,
        from: from.subtract(90, 'days'),
        reoccur: '90',
      };
    case 'last365':
      return {
        to,
        from: from.subtract(365, 'days'),
        reoccur: '365',
      };
    case 'monthly':
      return {
        to: moment().endOf('month'),
        from: from.startOf('month'),
        reoccur: 'MONTHLY',
      };
    case 'annual':
      return {
        to: moment().endOf('year'),
        from: from.startOf('year'),
        reoccur: 'ANNUAL',
      };
    case 'custom':
      return {
        to: null,
        from: null,
        reoccur: null,
      };
    default:
      return {
        to: null,
        from: null,
        reoccur: null,
      };
  }
};

export const groupOptions = ({ t = () => {}, model }) => {
  let options = [
    { value: 'workspaceId', label: t(keys.common.WORKSPACE) },
    { value: 'creatorId', label: t(keys.common.CREATOR) },
    { value: 'participants', label: t(keys.common.PARTICIPANTS) },
  ];

  switch (model) {
    case 'inspection':
      options.push(
        { value: 'inspectionType', label: t(keys.common.TYPE) },
        { value: 'vehicleId', label: t(keys.common.ASSET) },
        { value: 'passed', label: t(keys.assessments.PASS) },
        { value: 'isDraft', label: t(keys.common.STATUS) },
      );
      break;
    case 'audit':
      options.push(
        { value: 'auditType', label: t(keys.common.TYPE) },
        { value: 'passed', label: t(keys.assessments.PASS) },
      );
      break;
    case 'correctiveAction':
      options.push(
        { value: 'assigneeId', label: t(keys.common.ASSIGNEE) },
        { value: 'type', label: t(keys.common.TYPE) },
        { value: 'status', label: t(keys.common.STATUS) },
      );
      break;
    case 'actionItem':
      options.push(
        { value: 'assigneeId', label: t(keys.common.ASSIGNEE) },
        { value: 'type', label: t(keys.common.TYPE) },
        { value: 'status', label: t(keys.common.STATUS) },
        { value: 'vehicleId', label: t(keys.common.ASSET) },
      );
      break;
    case 'observation':
      options.push(
        { value: 'location', label: t(keys.common.LOCATION) },
        { value: 'dateTime', label: t(keys.common.DATE) },
        { value: 'status', label: t(keys.common.STATUS) },
      );
      break;
    case 'incident':
      options.push(
        { value: 'type', label: t(keys.common.TYPE) },
        { value: 'subtype', label: t(keys.common.SUBTYPE) },
        { value: 'status', label: t(keys.common.STATUS) },
        { value: 'dateOfIncident', label: t(keys.incidents.DATE_OF_INCIDENT) },
        { value: 'lostTime', label: t(keys.incidents.LOST_TIME) },
        { value: 'nearMiss', label: t(keys.incidents.NEAR_MISS) },
        { value: 'vehicleId', label: t(keys.common.ASSET) },
        { value: 'location', label: t(keys.common.LOCATION) },
      );
      break;
    case 'timecard':
    case 'expense':
      options = options?.filter((o) => o?.value !== 'participants');
      break;
    default:
      break;
  }

  return options;
};

export const getFieldOptions = ({ t = () => {}, model }) => {
  switch (model) {
    case 'inspection':
      return [
        {
          value: 'inspectionType',
          label: t(keys.common.TYPE),
        },
        {
          value: 'templateId',
          label: t(keys.common.TEMPLATE),
        },
        {
          value: 'creatorId',
          label: t(keys.common.CREATOR),
        },
      ];
    case 'timecard':
    case 'expense':
      return [
        {
          value: 'creatorId',
          label: t(keys.common.CREATOR),
        },
      ];
    default:
      break;
  }
};

export const getFilterValues = ({ field, types, templates, users }) => {
  switch (field) {
    case 'templateId':
      return templates.map((t) => {
        let templateIds = [t?.id];
        if (!!t?.originalTemplate) {
          templateIds = [
            ...templateIds,
            ...t?.originalTemplate?.revisions?.map((r) => r?.id),
          ];
        }

        return { ...t, value: templateIds, label: t?.title };
      });
    case 'inspectionType':
      return Object.values(types)?.map((t) => ({
        ...t,
        label: t?.name,
        value: [t?.key],
      }));
    case 'creatorId':
      return users.map((u) => {
        return {
          ...u,
          value: [u?.id],
          label: `${u?.firstName} ${u?.lastName}`,
        };
      });
    default:
      return [];
  }
};

export const formatFilterString = ({
  field,
  value,
  t = () => {},
  types,
  templates,
  users,
}) => {
  let fieldStr = '';
  let valueStr = '';

  switch (field) {
    case 'inspectionType':
      fieldStr = t(keys.common.TYPE);
      valueStr = value
        ?.map(
          (type) =>
            `${types[type === 'VEHICLE' ? 'fleet' : type.toLowerCase()]?.name}`,
        )
        .join(', ');
      break;
    case 'templateId':
      fieldStr = t(keys.common.TEMPLATE);
      valueStr = value
        ?.map((v) => `${templates?.find((t) => v?.includes(t?.id))?.title}`)
        .join(', ');
      break;
    case 'creatorId':
      fieldStr = t(keys.common.CREATOR);
      valueStr = value
        ?.map((v) => {
          const userToShow = users?.find((u) => v?.includes(u?.id));
          return `${userToShow?.firstName} ${userToShow?.lastName}`;
        })
        .join(', ');
      break;
    default:
      break;
  }

  return t(keys.report.FILTER_STRING, { field: fieldStr, value: valueStr });
};

export const getFilterDisplayValue = (
  filter,
  { activeTemplateTypes, defaultAssessmentTypes, templates, users },
) => {
  if (filter?.field === 'inspectionType') {
    return filter?.value?.map((v) => ({
      value: v,
      label: {
        ...activeTemplateTypes,
        ...defaultAssessmentTypes,
      }?.[v !== 'VEHICLE' ? v?.toLowerCase() : 'fleet']?.name,
    }));
  } else if (filter?.field === 'templateId') {
    return filter?.value?.map((v) => ({
      value: v,
      label: templates?.find((t) => v.includes(t?.id))?.title,
    }));
  } else if (filter?.field === 'creatorId') {
    return filter?.value?.map((v) => {
      const displayUser = users?.find((u) => v.includes(u?.id));
      return {
        value: v,
        label: `${displayUser?.firstName} ${displayUser?.lastName}`,
      };
    });
  } else {
    return null;
  }
};

export const handleModelChange = (model, options) => {
  let filters = [];
  let actualModel = model;

  switch (model) {
    case 'inspection':
      filters.push({
        field: 'inspectionType',
        operator: 'notIn',
        value: ['OBSERVATION', 'AUDIT', 'INCIDENT'],
        default: true,
      });
      break;
    case 'audit':
      filters.push({
        field: 'inspectionType',
        operator: 'eq',
        value: ['AUDIT'],
        default: true,
      });
      actualModel = 'inspection';
      break;
    case 'actionItem':
      filters.push({
        field: 'type',
        operator: 'notIn',
        value: [
          'OBSERVATION',
          'AUDIT',
          'INCIDENT',
          'INCIDENT_COMMENT',
          'CPA-INTERIM',
          'CPA-FINAL',
          'CPA-ACCEPT',
          'CPA-REVIEW',
        ],
        default: true,
      });
      break;
    case 'correctiveAction':
      filters.push({
        field: 'type',
        operator: 'in',
        value: ['CPA-INTERIM', 'CPA-FINAL', 'CPA-ACCEPT', 'CPA-REVIEW'],
        default: true,
      });
      actualModel = 'actionItem';
      break;
    default:
      break;
  }

  return {
    ...options,
    groups: [],
    model: actualModel,
    refModel: model,
    filters,
  };
};

export const roundFraction = (fraction, numberOfDecimalPlaces = 2) => {
  const percent = fraction * 100;
  if (percent >= 1) {
    return Math.round(percent); // no decimals
  } else {
    return (
      Math.round(percent * 10 ** numberOfDecimalPlaces) /
      10 ** numberOfDecimalPlaces
    );
  }
};

export const getReoccuranceProps = ({ to, from, reoccur: occurance }) => {
  const now = moment();
  switch (occurance) {
    case '30':
      return {
        interval: 'days',
        frequency: '30',
        nextRunAt: now.add(30, 'days').toISOString(),
      };
    case '90':
      return {
        interval: 'days',
        frequency: '90',
        nextRunAt: now.add(90, 'days').toISOString(),
      };
    case '365':
      return {
        interval: 'days',
        frequency: '365',
        nextRunAt: now.add(365, 'days').toISOString(),
      };
    case 'MONTHLY':
      return {
        interval: 'month',
        frequency: '1',
        nextRunAt: now.add(1, 'month').startOf('month').toISOString(),
      };
    case 'ANNUAL':
      return {
        interval: 'year',
        frequency: '1',
        nextRunAt: now.add(1, 'year').startOf('year').toISOString(),
      };
    case true:
      const toMoment = moment(to);
      const fromMoment = moment(from);
      const diff = toMoment.diff(fromMoment, 'days');

      const newMoment = toMoment.clone().add(diff === 0 ? 1 : diff, 'days');

      return {
        interval: 'days',
        frequency: `${diff === 0 ? 1 : diff}`,
        nextRunAt: newMoment.toISOString(),
      };
    default:
      return {
        interval: null,
        frequency: null,
        nextRunAt: null,
      };
  }
};

export const statusLookup = {
  ...actionItemStatus,
  ...incidentStatus,
  ...observationStatus,
};

export const handleExport = async ({
  data,
  options,
  title,
  supportingData: { allUsers, allWorkspaces, allVehicles },
}) => {
  const XLSX = await import('xlsx');
  const workbook = XLSX.utils.book_new();

  if (!!!data && !!!options) {
    showToast({
      title: 'No usage events found',
      message: 'No data to export',
      variant: 'warning',
    });
    return;
  }

  const formatValue = (value, key) => {
    switch (key) {
      case 'participants':
      case 'creatorId':
      case 'assigneeId':
        const user = allUsers.find((u) => u.id === value);
        return !!user ? `${user?.firstName} ${user?.lastName}` : 'Anonymous';
      case 'workspaceId':
        const workspace = allWorkspaces.find((w) => w.id === value);
        return !!workspace ? workspace?.title : 'No Workspace';
      case 'vehicleId':
        const vehicle = allVehicles.find((v) => v.id === value);
        return !!vehicle ? vehicleString(vehicle) : 'Archived';
      default:
        return value;
    }
  };

  let index = 0;

  const timeRange = options[0].timeRange;
  const { to, from } = timeRange;

  const filename = `${title}-${moment(from).format('YYYYMMDD')}_${moment(
    to,
  ).format('YYYYMMDD')}.xlsx`;

  for (const option of options) {
    const dataset = data[option.key];
    const { timeRange, filters, model } = option;
    const { to, from, key: timeField } = timeRange;

    const filterString = filters
      .map((f) => {
        if (f?.default) {
          return '';
        } else {
          return `${f.field} is ${f.value}`;
        }
      })
      .join(',');

    const sheetdata = [
      ['Title', 'From', 'To', 'Date Key', 'Filters', 'Total', 'Module'],
      [title, from, to, timeField, filterString, dataset?.total, model],
      [], // empty row for separation
    ];

    const { groups } = dataset;

    for (const [key, group] of Object.entries(groups)) {
      sheetdata.push([]);
      sheetdata.push([`By ${key}`, 'Count']);
      for (const row of group) {
        sheetdata.push([formatValue(row.value, key), row.count]);
      }
    }

    const worksheet = XLSX.utils.aoa_to_sheet(sheetdata);
    XLSX.utils.book_append_sheet(workbook, worksheet, `Report${index}`);
    index = index + 1;
  }

  try {
    XLSX.writeFile(workbook, filename);

    showToast({
      title: 'Export complete',
      message: `Downloaded Complete`,
      variant: toastVariant.info,
    });
  } catch (err) {
    showToast({
      title: 'Export failed',
      message: err.message || 'There was a problem exporting',
      variant: toastVariant.danger,
    });
  }
};
